import { useState, useEffect, useRef } from "react";
import SideFilter from "../../components/side-filter";
import ServiceCardLayout from "./serviceCardLayout";
import { useSearchLocation } from "../../store/searchLocation";
import { useServiceStore } from "../../store/serviceStore";
import { useUserStore } from "../../store/userStore";
import toast from "react-hot-toast";
import { useUserChoiceStore } from "../../store/userStore";
import { ChevronDown } from "lucide-react";
import { useMetaDataStore } from "../../store/metadataStore";
import { useMetaStore } from "../../store/data";
import { SEO } from "../../components/SEO";
import { createHmacSignature } from "../../utils/hmac-signature";
import { API } from "../../utils/axios";
import Loading from "../../components/loader";
import { XCircle } from "lucide-react";

const staticStates = [
  {
    zone_id: 610,
    name: "Ontario",
    cities: [
      { city_id: 1, city_name: "Toronto" },
      { city_id: 2, city_name: "Mississauga" },
      { city_id: 3, city_name: "Brampton" },
      { city_id: 4, city_name: "Ottawa" },
      { city_id: 5, city_name: "Hamilton" },
      { city_id: 6, city_name: "London" },
      { city_id: 7, city_name: "Kitchener" },
      { city_id: 8, city_name: "Waterloo" }
    ]
  },
  {
    zone_id: 603,
    name: "British Columbia",
    cities: [
      { city_id: 9, city_name: "Vancouver" },
      { city_id: 10, city_name: "Surrey" },
      { city_id: 11, city_name: "Burnaby" },
      { city_id: 12, city_name: "Richmond" },
      { city_id: 13, city_name: "Victoria" },
      { city_id: 14, city_name: "Kelowna" }
    ]
  },
  {
    zone_id: 602,
    name: "Alberta",
    cities: [
      { city_id: 15, city_name: "Calgary" },
      { city_id: 16, city_name: "Edmonton" },
      { city_id: 17, city_name: "Red Deer" },
      { city_id: 18, city_name: "Lethbridge" },
      { city_id: 19, city_name: "Airdrie" },
      { city_id: 20, city_name: "Chestermere" }
    ]
  }
];

const staticServices = [
  { servicetype_id: 1, name: "Physiotherapy" },
  { servicetype_id: 2, name: "Chiropractic" },
  { servicetype_id: 3, name: "Acupuncture" },
  { servicetype_id: 4, name: "Massage Therapy" }
];

export default function ExploreBusinesses() {
  const { searchLocation, updateSearchLocation } = useSearchLocation();
  const user = useUserStore((state) => state.user);
  const servicesData = useServiceStore((state) => state.services);
  const setUserChoice = useUserChoiceStore((state) => state.setUserChoice);
  const updateUser = useUserStore((state) => state.updateUser);
  const setservicesData = useServiceStore((state) => state.setServices);
  const [selectService, setSelectService] = useState(user?.serviceType || "");
  const [userSelectedService, setUserSelectedService] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showClearButton, setShowClearButton] = useState(false);
  const [location, setLocation] = useState("");
  const [locationId, setLocationId] = useState(0);
  const locationRef = useRef("");
  const latitudeRef = useRef(0);
  const longitudeRef = useRef(0);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const { states } = useMetaDataStore();
  const { serviceTypes } = useMetaStore();

  const filteredData = servicesData.filter((item) => {
    return selectService === "" || item.businessType === selectService;
  });

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.");
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        latitudeRef.current = latitude;
        longitudeRef.current = longitude;
        updateSearchLocation({
          latitude,
          longitude,
          liveLocation: true,
          locationId: 0,
          location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
        });
        setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        setError(null);
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error("Error getting location:", error);
        let errorMessage = "Unable to get your location";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage =
              "Click the 🔒 icon in the URL → Reset Permissions → Reload";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "Location request timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred.";
            break;
        }

        toast.success(errorMessage, {
          duration: 5000,
        });
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      },
    );
  };

  async function clearSearchResults() {
    setLocation("");
    setUserSelectedService("");
    setShowClearButton(false);
    updateSearchLocation({
      location: "",
      locationId: 0,
      userSelectedService: 0,
    });
    setLoading(true);
    try {
      const key = await createHmacSignature("defaulters");
      const businessData = await API.post("/business/data", { data: key });
      setservicesData(businessData.data.businesses);
    } catch (err) {
      console.log("error in clearSearchResults error", err);
    } finally {
      setLoading(false);
    }
  }

  async function getSearchBusinesses() {
    if (!searchLocation.location) {
      setError("Select Location");
      return;
    }
    if (!searchLocation.userSelectedService) {
      setError("Select a service to find businesses");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const searchkey = await createHmacSignature("searchBL-LMW");
      const searchBusinessData = await API.post(
        `/business/search?q=${searchLocation.locationId}&latitude=${searchLocation.latitude}&longitude=${searchLocation.longitude}&radius=${global.config.RADIUS}&liveLocation=${searchLocation.liveLocation}&service=${searchLocation.userSelectedService}`,
        { data: searchkey },
      );
      setservicesData(searchBusinessData.data.businesses || []);
      setShowClearButton(true);
    } catch (error) {
      console.log("getSearchBusinesses error", error);
    } finally {
      setLoading(false);
    }
  }

  const handleSelectService = (data: string) => {
    setSelectService(data);
    updateUser({ serviceType: data });
  };

  async function getDefaultBusinesses() {
    try {
      const key = await createHmacSignature("defaulters");
      const businessData = await API.post("/business/data", { data: key });
      setservicesData(businessData.data.businesses);
    } catch (err) {
      console.log("getDefaultBusinesses error", err);
    }
  }

  useEffect(() => {
    if (searchLocation.userSelectedService) {
      setUserSelectedService(String(searchLocation.userSelectedService));
      setError(null);
    }
  }, []);

  useEffect(() => {
    if (searchLocation.liveLocation) {
      getCurrentLocation();
    } else if (searchLocation.location) {
      setLocation(searchLocation.location);
      setError(null);
      locationRef.current = searchLocation.location;
    }
  }, []);

  useEffect(() => {
    if (servicesData.length === 0) {
      getDefaultBusinesses();
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="w-full p-2 min-h-screen">
        <SEO
          title="Increase Your Online Chiropractic Clinic Bookings in Canada | Last Minute Wellness"
          description="Increase bookings for chiropractic clinic services with Last Minute Wellness. Book online, get fast relief from pain, and enjoy expert care at your convenience."
          url="https://lastminutewellness.ca/serviceList"
          image="https://lastminutewellness.ca/about-banner.png"
        />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Sidebar (Fixed/Sticky) */}
          <div className="lg:col-span-3 col-span-12">
            <div
              className="
      sticky top-28
      space-y-6 p-4
      max-h-[calc(100vh-7rem)]
      overflow-y-auto
      bg-white
      scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
    "
            >
              {/* Search Feature Block */}
              <div className="flex flex-col gap-4 bg-white border border-gray-200  rounded-xl shadow-sm p-3">
                {/* Location Input */}
                <div className="relative w-full">
                  <div
                    className="relative w-full max-w-md mx-auto"
                    ref={dropdownRef}
                  >
                    {/* Input-style selector */}
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full border border-blue-300 rounded-lg pl-14 pr-12 py-3 text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:outline-none cursor-pointer"
                        // placeholder="Enter city, state or zipcode"
                        placeholder="Select City"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        onClick={() => setIsOpen(!isOpen)}
                        readOnly
                      />

                      <ChevronDown
                        onClick={() => setIsOpen(!isOpen)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {/* Dropdown menu */}
                    {isOpen && (
                      <div className="absolute left-0 top-[110%] w-full bg-white shadow-xl rounded-2xl border border-blue-100 z-50 max-h-64 overflow-y-auto animate-in fade-in duration-150">
                        {staticStates.map((state) => (
                          <div key={state.zone_id}>
                            <div className="bg-blue-50 text-blue-600 font-semibold text-sm sm:text-base px-4 py-2">
                              {state.name}
                            </div>

                            {state.cities.map((city) => (
                              <div
                                key={city.city_id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setLocation(city.city_name);
                                  setLocationId(city.city_id);
                                  updateSearchLocation({
                                    location: city.city_name,
                                    locationId: city.city_id,
                                    latitude: 0,
                                    longitude: 0,
                                    liveLocation: false,
                                  });
                                  setError(null);
                                  setIsOpen(false);
                                }}
                                className={`px-4 py-2 text-gray-700 hover:bg-blue-100 cursor-pointer text-sm sm:text-base transition ${
                                  locationId === city.city_id
                                    ? "bg-blue-100 font-semibold text-blue-700"
                                    : ""
                                }`}
                              >
                                {city.city_name}
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Live Location Button */}
                  <button
                    type="button"
                    /* Added 'group' and 'relative' to trigger the tooltip */
                    className="group absolute top-1/2 left-2 -translate-y-1/2 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-300 transition disabled:opacity-50"
                    onClick={getCurrentLocation}
                    disabled={isLoadingLocation}
                  >
                    {isLoadingLocation ? (
                      <svg
                        className="animate-spin h-4 w-4 text-blue-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                    )}

                    {/* Tooltip Wrapper */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 scale-50 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 pointer-events-none z-[60]">
                      <div className="relative bg-gray-900 text-white text-[10px] font-medium py-2 px-3 rounded-lg whitespace-nowrap shadow-xl">
                        Use Current Location
                        {/* Tooltip Arrow pointing up to the button */}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                      </div>
                    </div>
                  </button>
                </div>

                {/* Service Select */}
                <div className="w-full">
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-3 focus:ring-2 focus:ring-[#d4a373] outline-none"
                    onChange={(e) => {
                      setUserSelectedService(e.target.value);
                      setUserChoice({ selectedService: e.target.value });
                      setError(null);
                      updateSearchLocation({
                        userSelectedService: Number(e.target.value),
                      });
                    }}
                    value={userSelectedService}
                  >
                    <option value="">Select Service</option>
                    {staticServices.map((service) => (
                      <option
                        key={service.servicetype_id}
                        value={service.servicetype_id}
                      >
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Button */}
                <div className="w-full">
                  {loading ? (
                    <Loading size={60} />
                  ) : (
                    <button
                      type="button"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-lg shadow-md transition font-medium"
                      onClick={() => getSearchBusinesses()}
                    >
                      Search
                    </button>
                  )}
                  {showClearButton && (
                    <button
                      onClick={() => clearSearchResults()}
                      className="mt-2 group flex items-center gap-1.5 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-400 transition-all hover:text-rose-500"
                    >
                      <XCircle className="h-3.5 w-3.5 transition-transform group-hover:rotate-90" />
                      <span>Clear search results</span>
                    </button>
                  )}
                </div>

                {error && (
                  <div className="text-red-600 text-sm p-2 border border-red-300 rounded-md bg-red-50">
                    {error}
                  </div>
                )}
              </div>

              {!isMobile && (
                <div className="lg:block hidden">
                  <SideFilter
                    userSelectedService={userSelectedService}
                    selectService={selectService}
                    setSelectService={handleSelectService}
                  />
                </div>
              )}
            </div>

            {/* 📱 Mobile version (non-sticky, collapsible) */}
            {isMobile && (
              <div className="lg:hidden block p-4">
                <details className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <summary className="cursor-pointer p-3 text-blue-600 font-medium">
                    Filters & Search
                  </summary>
                  <div className="mt-2 space-y-6 p-3">
                    <SideFilter
                      userSelectedService={userSelectedService}
                      selectService={selectService}
                      setSelectService={handleSelectService}
                    />
                  </div>
                </details>
              </div>
            )}
          </div>

          {/* Services + Pagination (Scrollable) */}
          <div className="lg:col-span-9 col-span-12">
            <div className="flex items-center justify-between px-10 py-4 mb-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                    Search Results
                  </h2>
                  <p className="text-sm font-normal text-[#333333]">
                    {filteredData.length === 0
                      ? "No matches found"
                      : `${filteredData.length} ${
                          filteredData.length === 1 ? "result" : "results"
                        } found`}
                  </p>
                </div>
              </div>
            </div>

            {servicesData.length === 0 && (
              <div className="flex flex-col items-center justify-center text-gray-600 bg-gray-50 py-12 rounded-xl mx-10 my-6">
                <svg
                  className="w-12 h-12 text-gray-400 mb-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 17v-2a4 4 0 014-4h6M9 13h.01M9 9h.01M9 5h.01"
                  />
                </svg>
                <p className="text-lg font-medium">No services available</p>
                <p>
                  Please enter correct city, state or zipcode for better
                  results.
                </p>
              </div>
            )}

            <div className="relative z-0">
              {(() => {
                if (servicesData.length === 0) return null;

                if (filteredData.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center text-center text-gray-700 bg-gray-50 py-12 px-6 rounded-xl mx-4 sm:mx-10 my-6 shadow-sm">
                      <svg
                        className="w-12 h-12 text-gray-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 17v-2a4 4 0 014-4h6M9 13h.01M9 9h.01M9 5h.01"
                        />
                      </svg>
                      <p className="text-lg font-semibold mb-1">
                        No services match your filters
                      </p>
                      <p className="text-sm text-gray-600 mb-1">
                        Please enter the correct city, state, or ZIP code for
                        better results.
                      </p>
                      <p className="text-sm text-gray-500">
                        Try adjusting your filters or search again.
                      </p>
                    </div>
                  );
                }

                // Responsive grid for results
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-4 sm:px-6 md:px-8">
                    {filteredData.map((item, index) => (
                      <div key={index}>
                        <ServiceCardLayout item={item} />
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
